import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import React, { useState, useRef, useEffect } from 'react';
import { Member } from '@/utils/types';

interface InviteMemberProps {
    members: Member[];
    inviteMembers: (members: Member[]) => void
    error: boolean
    setoverlayComp: (val: boolean) => void
    setResultOverlayComp: (text: string | null) => void
    backToMembers: () => void
}

export default function InviteMember(props: InviteMemberProps) {
    const [loading, setloading] = useState<boolean>(false);
    const [submitted, setSubmitted] = useState<boolean>(false);
    const [formValues, setFormValues] = useState({
        firstName: '',
        lastName: '',
        email: '',
        graduationYear: '',
        school: '',
        memberType: 'student',
    });

    const [formErrors, setFormErrors] = useState({
        firstName: '',
        lastName: '',
        email: '',
        graduationYear: '',
    });

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormValues({ ...formValues, [name]: value });
    };

    const validateForm = () => {
        const errors: any = {};
        if (!formValues.firstName.trim()) errors.firstName = 'First Name is required';
        if (!formValues.lastName.trim()) errors.lastName = 'Last Name is required';
        if (!formValues.email.trim() || !/\S+@\S+\.\S+/.test(formValues.email)) {
            errors.email = 'Valid Email is required';
            //do check to see if the email is already in the list
        }
        else if (props.members.some(x => x.email === formValues.email)) {
            errors.email = 'A member already exists with this email';
        }
        if (formValues.memberType == "student" && (!formValues.graduationYear.trim() || isNaN(Number(formValues.graduationYear)))) {
            errors.graduationYear = 'Graduation Year must be a number';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0; // Return true if no errors
    };

    const handleSubmit = () => {
        if (validateForm()) {
            const newM: Member = {
                name: `${formValues.firstName} ${formValues.lastName}`,
                school: formValues.school,
                membershipType: formValues.memberType,
                email: formValues.email,
                year: formValues.graduationYear === '' ? 0 : parseInt(formValues.graduationYear),
                id: null,
                lastSession: '',
                topPath: ''
            }
            props.inviteMembers([newM]);
            setSubmitted(true);
            setloading(true);
        }
    };

    useEffect(() => {
        if (submitted) {
            props.setResultOverlayComp("successfully added new member");
            setSubmitted(false);
            setTimeout(() => {
                props.setResultOverlayComp(null);
                props.backToMembers();
            }, 2000)
        }
    }, [props.members]);

    useEffect(() => {
        if (props.error && submitted) {
            setSubmitted(false);
            props.setResultOverlayComp("An error occured, please try again.")
            setTimeout(() => {
                props.setResultOverlayComp(null)
            }, 2000)
        }
    }, [props.error]);

    useEffect(() => {
        if (loading) {
            props.setoverlayComp(loading);
        }
    }, [loading]);

    return (
        <div className="w-[100%]">
            <Box sx={{ width: '100%', boxShadow: '1', borderBottom: '1px solid rgba(0, 0, 0, 0.2)' }}>
                <Toolbar
                    sx={[
                        {
                            backgroundColor: "white",
                            pl: { sm: 2 },
                            pr: { xs: 1, sm: 1 },
                        }
                    ]}
                >
                    {(
                        <Typography
                            sx={{ flex: '1 1 100%', fontWeight: '450', textAlign: 'left' }}
                            variant="h6"
                            id="tableTitle"
                            component="div"
                        >
                            Invite New Member
                        </Typography>
                    )}
                </Toolbar>
            </Box>
            <Box sx={{ width: '100%', boxShadow: '1', padding: '1.5rem', backgroundColor: "white" }}>
                <div className='flex mb-8  text-left w-[100%]'>
                    <p className='mr-5 font-bold'>New Member Info</p>
                    <p className='text-customBlack'>Add their info and we will send an invite for them to join. The new member license is active after they sign in for the first time.</p>
                </div>
                <Box
                    component="form"
                    noValidate
                    autoComplete="off"
                    sx={{
                        display: 'flex',
                        flexDirection: 'column', // Ensures the email input is on the next line
                        gap: 3, // Adds spacing between rows and inputs
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 2, // Adds some spacing between the inputs
                        }}>
                        <TextField
                            label="First Name"
                            name="firstName"
                            value={formValues.firstName}
                            onChange={handleInputChange}
                            error={!!formErrors.firstName}
                            helperText={formErrors.firstName}
                            variant="outlined"
                            size="small"
                            fullWidth
                            sx={{ flex: 1 }}
                        />
                        <TextField
                            label="Last Name"
                            name="lastName"
                            value={formValues.lastName}
                            onChange={handleInputChange}
                            error={!!formErrors.lastName}
                            helperText={formErrors.lastName}
                            variant="outlined"
                            size="small"
                            fullWidth
                            sx={{ flex: 1 }}
                        />
                    </Box>
                    {/* Email Field */}
                    <TextField
                        label="Email"
                        name="email"
                        value={formValues.email}
                        onChange={handleInputChange}
                        error={!!formErrors.email}
                        helperText={formErrors.email}
                        variant="outlined"
                        size="small"
                        fullWidth
                    />

                    {/* Vertical List of Checkboxes */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'start',
                            color: 'rgba(0, 0, 0, 0.6)',
                            gap: 1, // Adds some spacing between the inputs
                        }}>
                        <Typography variant="body1">
                            Member Type
                        </Typography>
                        <RadioGroup
                            aria-labelledby="member-type"
                            value={formValues.memberType}
                            onChange={(e) =>
                                setFormValues({ ...formValues, memberType: e.target.value })
                            }
                            name="memberType"
                        >
                            <FormControlLabel value="student" control={<Radio />} label="Student" />
                            <FormControlLabel value="admin" control={<Radio />} label="Advisor" />
                        </RadioGroup>
                    </Box>
                    {formValues.memberType == "student" && <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 2, // Adds some spacing between the inputs
                        }}
                    >
                        <TextField
                            label="Graduation Year"
                            name="graduationYear"
                            value={formValues.graduationYear}
                            onChange={handleInputChange}
                            error={!!formErrors.graduationYear}
                            helperText={formErrors.graduationYear}
                            variant="outlined"
                            size="small"
                            fullWidth
                            sx={{ flex: 1 }}
                        />
                        <TextField
                            label="Department (optional)"
                            variant="outlined"
                            size="small"
                            name="school"
                            value={formValues.school}
                            onChange={handleInputChange}
                            fullWidth
                            sx={{ flex: 1 }} // Ensures equal width of the boxes
                        />
                    </Box>}
                </Box>
                <div className='w-[unset] justify-self-center mt-4 px-3 py-2 text-white cursor-pointer hover:bg-grayBlue-hover font-[300] bg-grayBlue rounded-md shadow-sm' onClick={handleSubmit}><p>Send Invite</p></div>
            </Box>
        </div>
    );
}